import { isNaN } from 'lodash';
import React, { useCallback, useRef, useState } from 'react';
import {
  ATMButton,
  ATMDropdown,
  ATMField,
  ATMForm,
  ATMInput,
  ATMModal,
} from 'shared-it-appmod-ui';
import { ToastError } from 'src/components/atoms/toaster/toaster.component';
import { RequestStatus } from 'src/constants';
import { useRequestContext } from 'src/contexts/request.context';
import { actionTypes } from 'src/ducks/request.duck';
import Lang from 'src/libraries/language';
import { RequestFlightCostFormSchema } from 'src/models/request.model';
import { getRequestStatus } from 'src/selectors/request.selector';

type IProps = {
  requestId: number;
  flightCost?: string;
  handleCostStatusSuccess?: (data) => void;
};

const FlightRequestEditCost: React.FC<IProps> = ({
  requestId,
  flightCost = '',
  handleCostStatusSuccess,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const { state, actions } = useRequestContext();
  const status = getRequestStatus(state, actionTypes.REQUEST_UPDATE_COST);
  const statusChange = getRequestStatus(
    state,
    actionTypes.REQUEST_UPDATE_STATUS
  );

  const handleSave = useCallback(() => {
    if (formRef && formRef.current) {
      formRef.current.handleSubmit();
    }
  }, [formRef]);

  const handleSubmit = useCallback(
    async (data) => {
      const result = await actions.updateFlightCostPUT(
        requestId,
        data.flightCost
      );

      if (result.payload && handleCostStatusSuccess) {
        handleCostStatusSuccess(Lang.MSG_REQUEST_UPDATE_COST_SUCCESS);
        actions.setRequestCost(
          requestId,
          data.flightCost,
          RequestStatus.Completed
        );
        setIsOpen(false);
      } else {
        ToastError(Lang.MSG_REQUEST_UPDATE_COST_FAILED);
      }
    },
    [requestId, actions, setIsOpen]
  );

  return (
    <ATMModal
      size="tiny"
      open={isOpen}
      onClose={() => setIsOpen(false)}
      trigger={
        <ATMDropdown.Item
          content={Lang.TTL_REQUEST_FLIGHT_EDIT_COST}
          onClick={() => setIsOpen(true)}
          icon="dollar"
        />
      }
    >
      <ATMModal.Header>{`${Lang.TTL_REQUEST_FLIGHT_EDIT_COST} - Request ${requestId}`}</ATMModal.Header>
      <ATMModal.Content>
        <ATMForm
          ref={formRef}
          onSubmit={handleSubmit}
          defaultValues={{ flightCost }}
          validationSchema={RequestFlightCostFormSchema}
        >
          {({ control, errors }) => (
            <ATMField
              name="flightCost"
              label={Lang.LBL_FLIGHT_COST}
              as={ATMInput}
              placeholder={Lang.LBL_ENTER}
              control={control}
              onBlur={(_, value) => {
                const temp = value
                  .replace('$', '')
                  .replace(',', '')
                  .replace(/[^0-9.]+/, '');

                const amount = parseFloat(temp);

                return !isNaN(amount)
                  ? `$${parseFloat(temp)
                      .toFixed(2)
                      .replace(/\d(?=(\d{3})+\.)/g, '$&,')}`
                  : '';
              }}
              error={errors.flightCost}
            />
          )}
        </ATMForm>
      </ATMModal.Content>
      <ATMModal.Actions>
        <ATMButton
          secondary
          content={Lang.LBL_CANCEL}
          onClick={() => {
            setIsOpen(false);
          }}
        />
        <ATMButton
          primary
          content={Lang.LBL_SAVE}
          onClick={handleSave}
          loading={status.fetching || statusChange.fetching}
          disabled={status.fetching || statusChange.fetching}
        />
      </ATMModal.Actions>
    </ATMModal>
  );
};

export default FlightRequestEditCost;
